import io from 'socket.io-client';
import store from '@/store/store';
import { CONNECT } from '@/app.config';
import UserService from '@/services/user.service';
import SceneService from '@/services/weather.service';
import { TokenService } from '@/services/storage.service';
import { validateInstallation, sanitizeInstallation } from '@/models/installations.model';
import { sanitizeDevice } from '@/models/devices.model';
import { validateSchedule } from '@/models/schedules.model';
import { validateScene } from '@/models/scenes.model';
import { consoleSuccess, consoleInfo } from '@/utils/console.utils';
import { isArray } from '@/utils/validate.utils';

let usersSocket = null;
let installationsSocket = {};

const SocketService = {
  connectUserSocket() {
    return new Promise(resolve => {
      // Compruebo si hay un socket de usuario,
      // si lo hay desconecto todos los sockets
      if (usersSocket) {
        this.disconnectAllSocket();
      }

      const token = TokenService.getToken('token');
      const appMode = store.getters['globalModule/getAppMode'];

      usersSocket = io(`${CONNECT[appMode].API_URL}users`, {
        path: CONNECT.SOCKET_PATH,
        transports: ['polling'],
        transportOptions: {
          polling: {
            extraHeaders: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      });

      /**
       * Evento cuando el usuario se conecta al socket
       */
      usersSocket.on('connect', () => {
        consoleSuccess(`Usuario conectado al socket`);
        resolve(true);
      });

      /**
       * Evento cuando el usuario se reconecta al socket
       *
       * @param {Number} attemptNumber - Número de veces que se ha reconectado
       */
      usersSocket.on('reconnect', attemptNumber => {
        consoleSuccess(`Usuario reconectado al socket ${attemptNumber} veces`);
      });

      /**
       * Evento cuando el usuario recibe permiso para controlar una instalación
       * Si hay error en la validación no la añado y muestro un error
       *
       * 1- Valido los datos de la instalación
       * 2- Conecto la nueva instalación por socket
       * 3- Añado la instalación al Store
       *
       * @param {Object} installation - Datos de la instalación
       */
      usersSocket.on('control-new-installation-allowed', async installation => {
        consoleSuccess(`Nueva instalación añadida ${installation._id}`);

        const installationID = installation._id;
        const data = validateInstallation(installation); // [1]

        if (data) {
          await this.connectInstallationSocket(installationID); // [2]
          store.dispatch('unitsModule/saveInstallation', { installationID, data }); // [3]
          SceneService.getWeather(installationID, data);
        }
      });

      /**
       * Evento cuando los permisos de un usuario en una instalación han cambiado
       * Si hay error en la validación no modifico la instalación y muestro un error
       *
       * 1- Valido los datos de la instalación
       * 2- Actualizo la instalación en el Store
       *
       * @param {Object} installation - Datos de la instalación
       */
      usersSocket.on('control-modified-permission-installation', installation => {
        consoleSuccess(`Cambio en los permisos de la instalación ${installation._id}`);

        const installationID = installation.installation_id;
        const data = {
          type: installation.user_type,
        };

        store.dispatch('unitsModule/updateInstallation', { installationID, data }); // [2]
      });

      /**
       * Evento cuando la instalación de un usuario ha sido eliminada
       *
       * 1- Desconecto la instalación del socket
       * 2- Elimino la instalación del Store
       *
       * @param {Object} installation - Datos de la instalación
       */
      usersSocket.on('control-deleted-installation', installation => {
        consoleSuccess(`Eliminada la instalación ${installation.installation_id}`);

        const installationID = installation.installation_id;
        this.disconnectInstallation(installationID); // [1]
        store.dispatch('unitsModule/deleteInstallation', installationID); // [2]
      });

      /**
       * Evento cuando un dispostivo ha sido añadido a una instalación
       * Si hay error en la validación no añado el dispositivo y muestro un error
       *
       * 1- Valido los datos de la instalación
       * 2- Actualizo la instalación en el Store
       *
       * @param {Object} device - Datos del dispositivo
       */
      usersSocket.on('control-new-device', device => {
        consoleSuccess(`Nuevo dispositivo añadido ${device.mac}`);

        const installationID = device.installation_id;
        const deviceMac = device.mac;
        const data = sanitizeDevice(device); // [1]

        if (data) {
          store.dispatch('unitsModule/saveDevice', { installationID, deviceMac, data }); // [2]
        }
      });

      /**
       * Evento cuando un dispositivo ha sido eliminado de una instalación
       *
       * 1- Elimino el dispositivo del Store
       *
       * @param {Object} device - Datos del dispositivo
       */
      usersSocket.on('control-deleted-device', device => {
        consoleSuccess(`Eliminado el dispositivo ${device.mac}`);

        const installationID = device.installation_id;
        const deviceMac = device.mac;
        store.dispatch('unitsModule/deleteDevice', { installationID, deviceMac }); // [1]
      });

      /**
       * Evento cuando el usuario ha recibido permiso para controlar un nuevo dispositivo
       * Si hay error en la validación no añado el dispositivo y muestro un error
       *
       * 1- Valido los datos de la instalación
       * 2- Actualizo la instalación en el Store
       *
       * @param {Object} device - Datos del dispositivo
       */
      usersSocket.on('control-added-permitted-devices', devices => {
        if (isArray(devices.permitted_devices)) {
          const installationID = devices.installation_id;

          devices.permitted_devices.forEach(mac => {
            consoleSuccess(`Nuevo dispositivo añadido ${mac}`);

            const deviceMac = mac;
            const data = {};

            store.dispatch('unitsModule/saveDevice', { installationID, deviceMac, data }); // [2]
          });
        }
      });

      /**
       * Evento cuando al usuario se le quita el permiso para controlar varios dispositivo
       *
       * 1- Elimino el dispositivo del Store
       *
       * @param {Object} device - Datos del dispositivo
       */
      usersSocket.on('control-deleted-permitted-devices', devices => {
        if (isArray(devices.deleted_devices)) {
          const installationID = devices.installation_id;

          devices.deleted_devices.forEach(mac => {
            consoleSuccess(`Eliminado el dispositivo ${mac}`);

            const deviceMac = mac;
            store.dispatch('unitsModule/deleteDevice', { installationID, deviceMac }); // [1]
          });
        }
      });

      // Evento cuando el usuario se desconecta del socket
      //
      // When a user has been discconnect can be by:
      // 1. The app is offline
      // 2. The backend is down (disconnected)
      // 3. The user has logged out in this app or other app
      // 4. The session has expired
      /**
       * Evento cuando el usuario se desconecta del socket
       * Compruebo si el motivo por el que el se ha desconectado
       * y muestro un error en caso necesario
       *
       * 1- Compruebo si el usuario se ha deslogueado
       * 2- Compruebo el estado del backend
       *
       * @param {String} reason - Motivo por el que el usuario se ha desconectado del socket
       */
      usersSocket.on('disconnect', async reason => {
        consoleInfo(`Usuario desconectado: ${reason}`);

        if (TokenService.getToken()) {
          await UserService.getUserData(); // [2]
        }
      });
    });
  },

  initLinkProcess(mac) {
    consoleInfo(`initLinkProcess: ${mac}`);
    // envio evento
    usersSocket.emit('linkDevice', { mac }, () => {
      consoleInfo(`(${mac}) LINK`);
    });
    // escucho evento de control
    usersSocket.on('link-end', data => {
      consoleInfo(`Evento de link-end: ${JSON.stringify(data)}`);
      // Launch linkEnd event
      document.dispatchEvent(new CustomEvent('linkEnd'));
      this.finishLinkProcess();
    });

    usersSocket.on('link-upgrade', data => {
      consoleInfo(`Evento de link-upgrade: ${JSON.stringify(data)}`);
      document.dispatchEvent(new CustomEvent('upgradeActive'));
    });
  },

  finishLinkProcess() {
    usersSocket.removeAllListeners('link-end');
    usersSocket.removeAllListeners('link-upgrade');
    consoleInfo('Elimino listeners delinkProcess');
  },

  initWebserverUpdates(mac) {
    consoleInfo(`initWebserverUpdates: ${mac}`);
    // No envio evento, solo escucho el socket
    // usersSocket.emit('linkDevice', { mac }, () => {
    //   consoleInfo(`(${mac}) LINK`);
    // });
    // escucho evento de control
    usersSocket.on('finished-ota-ws', data => {
      consoleInfo(`Evento de finished-ota-ws: ${JSON.stringify(data)}`);
      // Launch linkEnd event
      document.dispatchEvent(new CustomEvent('finishOTA'));
      this.finishOTA();
    });

    usersSocket.on('request-ota-ws', data => {
      consoleInfo(`Evento de request-ota-ws: ${JSON.stringify(data)}`);
      document.dispatchEvent(new CustomEvent('webserverUpdates'));
    });
  },

  finishOTA() {
    usersSocket.removeAllListeners('finishOTA');
    usersSocket.removeAllListeners('webserverUpdates');
    consoleInfo('Elimino listeners: webserverUpdates y finishOTA');
  },

  connectInstallationSocket(installationID) {
    return new Promise(resolve => {
      // Compruebo si la instalación tiene un socket abierto,
      // si lo tiene, lo desconecto
      if ({}.hasOwnProperty.call(installationsSocket, installationID)) {
        this.disconnectInstallation(installationID);
      }

      const token = TokenService.getToken('token');
      const appMode = store.getters['globalModule/getAppMode'];

      const installationSocket = io(
        `${CONNECT[appMode].API_URL}${installationID}::${CONNECT.SCOPE}`,
        {
          path: CONNECT.SOCKET_PATH,
          transports: ['polling'],
          transportOptions: {
            polling: {
              extraHeaders: {
                Authorization: `Bearer ${token}`,
              },
            },
          },
        }
      );

      installationSocket.io.opts.transportOptions.polling.extraHeaders.Authorization = `Bearer ${token}`;

      // Evento cuando la instalación se conecta al socket
      installationSocket.on('connect', () => {
        consoleSuccess(`${installationID} - Instalación conectada al socket`);
        resolve(true);
      });

      // Evento cuando la instalación se reconecta al socket
      installationSocket.on('reconnect', attemptNumber => {
        consoleSuccess(
          `${installationID} - Instalación reconectada al socket ${attemptNumber} veces`
        );
      });

      // Evento cuando recibimos cambios de un dispositivo
      installationSocket.on('device-data', device => {
        const deviceMac = device.mac;
        const data = sanitizeDevice(device.data);

        console.log(device.data);

        if (data) {
          store.dispatch('unitsModule/updateDevice', { installationID, deviceMac, data });
        }
      });

      // Evento cuando recibimos cambios en una instalación
      installationSocket.on('data-installation-edited', installation => {
        const data = sanitizeInstallation(installationID, installation);

        console.log(installation);

        if (data) {
          store.dispatch('unitsModule/updateInstallation', { installationID, data });
        }
      });

      // Evento cuando se crea una nueva programación
      installationSocket.on('data-create-schedule', schedule => {
        const data = validateSchedule(schedule.sch);

        if (data) {
          const scheduleID = data.id;
          store.dispatch('unitsModule/saveSchedule', { installationID, scheduleID, data });
        }
      });

      // Evento cuando se edita una programación
      installationSocket.on('data-edit-schedule', schedule => {
        const data = validateSchedule(schedule.sch);

        if (data) {
          const scheduleID = data.id;
          store.dispatch('unitsModule/updateSchedule', { installationID, scheduleID, data });
        }
      });

      // Evento cuando se elimina una programación
      installationSocket.on('data-delete-schedule', schedule => {
        const scheduleID = schedule.sch_id;
        store.dispatch('unitsModule/deleteSchedule', { installationID, scheduleID });
      });

      // Evento cuando se crea una nueva escena
      // Compruebo si existe la escena antes de añadirla
      installationSocket.on('data-create-scene', scene => {
        const sceneID = scene.scene._id;
        const storeScene = store.getters['unitsModule/getScene'](installationID, sceneID);

        if (storeScene) return;

        const data = validateScene(scene.scene);
        if (data) store.dispatch('unitsModule/saveScene', { installationID, sceneID, data });
      });

      // Evento cuando se edita una escena
      // Compruebo si existe la escena
      installationSocket.on('data-edit-scene', scene => {
        const sceneID = scene.scene._id;
        const storeScene = store.getters['unitsModule/getScene'](installationID, sceneID);

        if (!storeScene) return;

        const data = validateScene(scene.scene);
        if (data) store.dispatch('unitsModule/updateScene', { installationID, sceneID, data });
      });

      // Evento cuando se elimina una escena
      // Compruebo si existe la escena
      installationSocket.on('data-delete-scene', scene => {
        const sceneID = scene.sceneId;
        const storeScene = store.getters['unitsModule/getScene'](installationID, sceneID);

        if (!storeScene) return;

        store.dispatch('unitsModule/deleteScene', { installationID, sceneID });
      });

      // Evento cuando una instalación se desconecta del socket
      installationSocket.on('disconnect', reason => {
        consoleInfo(`${installationID} - Instalación desconectada: ${reason}`);
      });

      installationsSocket[installationID] = installationSocket;
    });
  },

  /**
   * Envía cambios en los valores de un dispositivo
   *
   * @param {*} installationID
   * @param {*} data
   */
  setDeviceValue(installationID, data) {
    return new Promise(async resolve => {
      // Compruebo si la instalación tiene el socket abierto y no está en modo demo
      if (
        {}.hasOwnProperty.call(installationsSocket, installationID) &&
        !store.getters['globalModule/isDemo']
      ) {
        await installationsSocket[installationID].emit('create-machine-event', data, () => {
          consoleInfo(`(${data.mac}) POST - ${data.property}: ${data.value}`);
        });
      }

      // Almacenos los cambios en el store
      const deviceData = {};
      deviceData[data.property] = data.value;

      store.dispatch('unitsModule/updateDevice', {
        installationID,
        deviceMac: data.mac,
        data: deviceData,
      });

      resolve(true);
    });
  },

  disconnectAllSocket() {
    /*
     * Desconecta el socked del usuario
     */
    if (usersSocket) {
      usersSocket.removeAllListeners('connect');
      usersSocket.removeAllListeners('link-end');
      usersSocket.removeAllListeners('link-upgrade');
      usersSocket.removeAllListeners('reconnect');
      usersSocket.removeAllListeners('control-new-installation-allowed');
      usersSocket.removeAllListeners('control-modified-permission-installation');
      usersSocket.removeAllListeners('control-deleted-installation');
      usersSocket.removeAllListeners('control-new-device');
      usersSocket.removeAllListeners('control-deleted-device');
      usersSocket.removeAllListeners('control-added-permitted-devices');
      usersSocket.removeAllListeners('disconnect');
      usersSocket.close();
      usersSocket = null;
      consoleInfo('El usersSocket se ha desconectado correctamente');
    }

    /*
     * Desconecta las instalaciones del socket
     */
    // eslint-disable-next-line no-restricted-syntax
    for (const installationID in installationsSocket) {
      if ({}.hasOwnProperty.call(installationsSocket, installationID)) {
        installationsSocket[installationID].removeAllListeners('connect');
        installationsSocket[installationID].removeAllListeners('reconnect');
        installationsSocket[installationID].removeAllListeners('device-data');
        installationsSocket[installationID].removeAllListeners('data-installation-edited');
        installationsSocket[installationID].removeAllListeners('data-create-schedule');
        installationsSocket[installationID].removeAllListeners('data-delete-schedule');
        installationsSocket[installationID].removeAllListeners('data-edit-schedule');
        installationsSocket[installationID].removeAllListeners('disconnect');
        installationsSocket[installationID].close();
        delete installationsSocket[installationID];
        consoleInfo(`La instalación ${installationID} se ha desconectado correctamente`);
      }
    }

    installationsSocket = {};
  },

  disconnectInstallation(installationID) {
    if ({}.hasOwnProperty.call(installationsSocket, installationID)) {
      installationsSocket[installationID].removeAllListeners('connect');
      installationsSocket[installationID].removeAllListeners('reconnect');
      installationsSocket[installationID].removeAllListeners('device-data');
      installationsSocket[installationID].removeAllListeners('data-installation-edited');
      installationsSocket[installationID].removeAllListeners('data-create-schedule');
      installationsSocket[installationID].removeAllListeners('data-delete-schedule');
      installationsSocket[installationID].removeAllListeners('data-edit-schedule');
      installationsSocket[installationID].removeAllListeners('disconnect');
      installationsSocket[installationID].close();
      delete installationsSocket[installationID];
      consoleInfo(`La instalación ${installationID} se ha desconectado correctamente`);
    }
  },
};

export default SocketService;
